import { Modal } from 'reactstrap';

import { UpsellingSection } from 'components/UpsellModal/UpsellingSection';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { toggleSendSelectionModal, toggleUpsellModal } from 'store/slices/modals';
import type { ISelection } from 'old-store/types/gallery';
import { ModalHeader } from 'components/Modal/ModalHeader';
import { ModalBody } from 'components/Modal/ModalBody';
import { getTranslationKey } from 'helpers/texting';
import { useEffect } from 'react';
import { trackEventAction } from 'old-store/actions';
import { IProductGroup } from 'modules/ScrShopNew/store/slices/products/types';
import { getProductGroupByID } from 'modules/ScrShopNew/store/slices/products/selectors';
import {
  prepareNewDesignModal,
  toggleSendSelectionModal as toggleNewSendSelectionModal
} from 'modules/ScrAppBookNew/store/ui';
import { selectActiveSelectionState } from 'store/selectors/activeSelection';
import { UpsellingSectionOld } from './UpsellingSectionOld';

import type { ITheme } from 'hooks/useTheme';

import styled, { css, ThemeProvider } from 'styled-components';

const StyledModal = styled(Modal)`
  .modal-header {
    background: #fff !important;
    color: #000 !important;
    border-bottom: 0 !important;
    .modal-title {
      padding: 4px 30px !important;
      text-transform: initial !important;
      width: 100% !important;
      &:before {
        content: none !important;
      }
    }
  }
  .modal-content {
    .close {
      background: #000 !important;
    }
  }
  .modal-body {
    color: #000 !important;
  }

  // White in every template and palette: the template sheets paint modal surfaces from
  // --modal-bg / --ui-background (off-white #F9F9F6 in light palettes) at higher specificity.
  &&&& .modal-content,
  &&&& .modal-body {
    background: #fff !important;
  }
`;

const StyledHeader = styled(ModalHeader)`
  padding-top: 30px;
  padding-bottom: 0;
  color: #000 !important;
  h5 {
    color: #000 !important;
  }
`;

// The redesign keeps the upsell modal black and white instead of the collection palette; the
// bestseller badge, its border and star read these keys.
const PLAIN_COLORS = { primary: '#000', secondary: '#fff', secondaryButtonFontColor: '#000' };

const plainTheme = (theme: ITheme): ITheme => ({
  ...theme,
  colors: { ...theme.colors, ...PLAIN_COLORS }
});

// `.sb-btn` paints from the palette in the legacy sheet; the redesign keeps it black.
const ButtonWrapper = styled.div<{ $plain: boolean }>`
  display: flex;
  justify-content: center;
  margin-top: 20px;

  ${({ $plain }) =>
    $plain &&
    css`
      .sb-btn,
      .sb-btn:hover,
      .sb-btn:focus,
      .sb-btn:active {
        background-color: #000 !important;
        border-color: #000 !important;
        color: #fff !important;
      }
    `}
`;

export const UpsellModal = () => {
  const collection = useAppSelector((state) => state.collection);
  const { currentSelection } = useAppSelector(selectActiveSelectionState);
  const digitalGroup = useAppSelector(getProductGroupByID)('digital') as IProductGroup;
  const selection = useAppSelector(selectActiveSelectionState);
  const { upsellModal } = useAppSelector((state) => state.modals);

  const { featureFlags } = collection?.collectionCreator || {};
  const { newDesign, newShop } = featureFlags || {};

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (upsellModal.isOpen) {
      dispatch(
        trackEventAction({
          name: 'modal-window-opened',
          payload: {
            modal_id: 'upsell',
            url: window.location.href
          }
        })
      );
    }
  }, [upsellModal.isOpen]);

  const toggleModal = () => dispatch(toggleUpsellModal());

  const getSelection = (): ISelection => {
    if (upsellModal.selectionId) {
      return (
        selection.all.find((selection) => selection._id === upsellModal.selectionId) ??
        currentSelection
      );
    }

    return currentSelection;
  };

  const isShopAndDigitalPricingListEnabled = collection?.digitalShop?.enabled && digitalGroup;

  return (
    <ThemeProvider theme={newDesign ? plainTheme : (theme: ITheme) => theme}>
      <StyledModal
        centered
        isOpen={upsellModal.isOpen}
        toggle={toggleModal}
        size="lg"
        zIndex={999999}
        className="upsell-modal"
      >
        <StyledHeader toggle={toggleModal}>
          {getTranslationKey('shop.upselling.selectPackage')}
        </StyledHeader>
        <ModalBody>
          {isShopAndDigitalPricingListEnabled && newShop && (
            <UpsellingSection selection={getSelection()} toggleSaveSelectionModal={toggleModal} />
          )}
          {isShopAndDigitalPricingListEnabled && !newShop && (
            <UpsellingSectionOld
              selection={getSelection()}
              toggleSaveSelectionModal={toggleModal}
            />
          )}

          {digitalGroup?.products.length === 0 && (
            <ButtonWrapper $plain={!!newDesign}>
              <button
                className="sb-btn"
                onClick={() => {
                  toggleModal();
                  if (newDesign) {
                    dispatch(prepareNewDesignModal('sendSelectionModal'));
                    dispatch(toggleNewSendSelectionModal());
                  } else {
                    dispatch(toggleSendSelectionModal());
                  }
                }}
              >
                {getTranslationKey('selections.saveSelectionModal.alternative')}
              </button>
            </ButtonWrapper>
          )}
        </ModalBody>
      </StyledModal>
    </ThemeProvider>
  );
};
